<template>
    <div class="file-upload-wrapper">
        <input
            type="file"
            ref="fileInput"
            @change="onFileChange"
            class="hidden-file-input"
            accept="{{ accept }}"
        />

        <div
            class="custom-upload-box"
            :style="{ height: height + 'px' }"
            @click="triggerFileInput"
        >
            <template v-if="imagePreview">
                <img
                    :src="imagePreview"
                    alt="Preview"
                    class="image-preview"
                />
                <button v-if="canDelete" class="delete-file-btn" @click.stop="deleteFile()" title="Remover imagem">
                    <fa icon="fa-solid fa-trash" />
                </button>
            </template>
            <template v-else>
                <span class="plus-icon">
                    <fa icon="fa-solid fa-plus" />
                </span>
                <p class="info-text">{{ infoText }}</p>
            </template>
        </div>
    </div>
</template>

<script setup>
    import { ref, watch, defineProps, defineEmits, onBeforeUnmount } from 'vue';

    const props = defineProps({
        modelValue: [File, String],
        infoText: {
            type: String,
            default: 'Clique para selecionar um arquivo',
        },
        accept: {
            type: String,
            default: 'image/*',
        },
        height: {
            type: String,
            default: '120',
        },
        canDelete: {
            type: Boolean,
            default: true,
        },
    });

    const emit = defineEmits(['update:modelValue']);

    const fileInput = ref(null);
    const file = ref(props.modelValue || null);
    const imagePreview = ref(null);
    let objectUrl = null;

    watch(
        () => props.modelValue,
        (newVal) => {
            file.value = newVal;
            updatePreview(newVal);
        },
        { immediate: true }
    );

    const triggerFileInput = () => {
        fileInput.value.click();
    };

    const onFileChange = (event) => {
        const selected = event.target.files[0];
        file.value = selected;
        emit('update:modelValue', selected);
        updatePreview(selected);
    };

    function updatePreview(fileOrUrl) {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }

        if (!fileOrUrl) {
            imagePreview.value = null;
            return;
        }

        // Se for File
        if (fileOrUrl instanceof File && fileOrUrl.type.startsWith('image/')) {
            objectUrl = URL.createObjectURL(fileOrUrl);
            imagePreview.value = objectUrl;
        }
        // Se for string (URL)
        else if (typeof fileOrUrl === 'string') {
            imagePreview.value = fileOrUrl;
        } else {
            imagePreview.value = null;
        }
    }

    function deleteFile() {
        file.value = null;
        imagePreview.value = null;
        emit('update:modelValue', null);
        if (fileInput.value) {
            fileInput.value.value = '';
        }
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
            objectUrl = null;
        }
    }

    onBeforeUnmount(() => {
        if (objectUrl) {
            URL.revokeObjectURL(objectUrl);
        }
    });
</script>

<style scoped>
    svg {
        width: auto;
        max-width: 100%;
    }

    .hidden-file-input {
        display: none;
    }

    .file-upload-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        cursor: pointer;
    }

    .custom-upload-box {
        width: 100%;
        height: 120px;
        border: 2px dashed #b3b5ba;
        border-radius: 12px;
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        opacity: 0.8;
        overflow: hidden;
        transition: all 0.3s;
    }

    .custom-upload-box:hover {
        opacity: 1;
        background-color: #eaeaea;
    }

    .delete-file-btn {
        color: #dc2626;
        padding: 6px;
        position: absolute;
        top: 4px;
        right: 4px;
        aspect-ratio: 1;
        border-radius: 50%;
        opacity: 70%;
        transition: all 0.3s ease-out;
    }

    .delete-file-btn:hover {
        background-color: #ffb2b2;
        opacity: 1;
    }

    .plus-icon {
        color: #082a9d;
        padding: 12px;
        background-color: #e8f0fb;
        border-radius: 8px;
    }

    .delete-file-btn .svg-inline--fa,
    .plus-icon .svg-inline--fa {
        color: currentColor;
        width: 1em;
    }

    .image-preview {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }

    .info-text {
        color: #666;
        font-size: 12px;
        text-align: center;
        margin: 8px auto 0;
    }

    .dark .custom-upload-box {
        border-color: #fff;
    }

    .dark .custom-upload-box:hover {
        background-color: #333;
    }

    .dark .plus-icon {
        background-color: #444;
        color: #fff;
    }

    .dark .info-text {
        color: #ccc;
    }
</style>
